import { useEffect, useRef, useState } from 'react';
import HeroSectionStyle from './style';
import { withDataContext, withLanguageContext } from '@/context';
import { useRouter } from 'next/navigation';

const LETTER_TIMES = [
	{ letter: 'a', time: 0,   delay: 500 },
	{ letter: '',  time: 2.2, delay: 0   },
	{ letter: '',  time: 5,   delay: 0   },
	{ letter: 'z', time: 6.9, delay: 500 },
	{ letter: 'b', time: 9.4, delay: 500 },
	{ letter: 'g', time: 11.4,delay: 500 },
	{ letter: 'd', time: 13.7,delay: 500 },
	{ letter: 'e', time: 16,  delay: 0   },
];


const HeroSection = () => {
	const videoRef = useRef(null);
	const [videoSrc, setVideoSrc] = useState(() => {
		if (typeof window === 'undefined') return '/videos/st_landing_final_lr.mp4';
		return window.matchMedia('(max-width: 1023px)').matches
			? '/videos/st_landing_final_9x16_lr.mp4'
			: '/videos/st_landing_final_lr.mp4';
	});

	const router = useRouter();
	const CUSTOM_ORDER = ['a', 'b', 'g', 'd', 'e', 'z'];
	const sortedLetterTimes = [...LETTER_TIMES].sort((a, b) => {
		const ai = CUSTOM_ORDER.indexOf(a.letter);
		const bi = CUSTOM_ORDER.indexOf(b.letter);
		if (ai === -1 && bi === -1) return 0;
		if (ai === -1) return 1;
		if (bi === -1) return -1;
		return ai - bi;
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 1023px)');
		const updateVideoSrc = (event) => {
			setVideoSrc(event.matches ? '/videos/st_landing_final_9x16_lr.mp4' : '/videos/st_landing_final_lr.mp4');
		};

		updateVideoSrc(mediaQuery);
		mediaQuery.addEventListener('change', updateVideoSrc);
		return () => mediaQuery.removeEventListener('change', updateVideoSrc);
	}, []);

	// Ensure autoplay works on mobile by explicitly starting playback after source is set.
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const applyAutoplayFlags = () => {
			// iOS Safari autoplay is sensitive to these flags being set synchronously.
			video.muted = true;
			video.playsInline = true;
			video.setAttribute('muted', '');
			video.setAttribute('playsinline', '');
			video.setAttribute('webkit-playsinline', '');
		};

		const tryPlay = async () => {
			applyAutoplayFlags();
			try {
				await video.play();
			} catch {
				// Autoplay can still be blocked by some browser policies.
			}
		};

		const runWithRetry = () => {
			let attempts = 0;
			const maxAttempts = 6;
			const delayMs = [0, 150, 350, 650, 1000, 1400];

			const tick = async () => {
				if (!videoRef.current) return;
				attempts += 1;
				await tryPlay();

				// Stop retrying once it looks like playback is active.
				if (videoRef.current && !videoRef.current.paused && !videoRef.current.ended) return;
				if (attempts < maxAttempts) {
					const ms = delayMs[attempts] ?? 300;
					setTimeout(tick, ms);
				}
			};

			// If we already have enough data, start immediately; otherwise wait for canplay.
			if (video.readyState >= 2) {
				void tick();
				return;
			}

			video.addEventListener('canplay', () => {
				void tick();
			}, { once: true });
		};

		// Some iOS cases are picky until the element is in view.
		try {
			const obs = new IntersectionObserver((entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					obs.disconnect();
					runWithRetry();
				}
			}, { threshold: 0.15 });
			obs.observe(video);

			// Fallback: also start shortly after mount in case IntersectionObserver doesn't fire.
			setTimeout(() => runWithRetry(), 300);
		} catch {
			runWithRetry();
		}
	}, [videoSrc]);

	const handleClick = (letter) => {
		const REDIRECT_LETTERS = ['b', 'g', 'd', 'e', 'z'];
		if (letter) {
			if (REDIRECT_LETTERS.includes(letter)) {
				router.push(`/buildings/${letter}`);
			}
		}
	};

	return (
		<HeroSectionStyle>
			<div className="platform-hero-section">
				<div className="platform-hero-points">
					{sortedLetterTimes.map(({ letter }, index) => {
						if (letter === 'a') {
							return (
								<span key={index} className="coming-soon-wrapper">
									<button type="button" disabled>
										{letter}
									</button>
									<span className="coming-soon-tooltip">Coming Soon</span>
								</span>
							);
						}
						return (
							<button
								key={index}
								type="button"
								onClick={() => handleClick(letter)}
							>
								{letter}
							</button>
						);
					})}
				</div>

				<video
					ref={videoRef}
					src={videoSrc}
					muted
					playsInline
					webkit-playsinline=""
					autoPlay
					loop
					preload="auto"
					controls={false}
					style={{ width: '100%', height: 'auto', display: 'block' }}
				/>
			</div>
		</HeroSectionStyle>
	);
};

export default withDataContext(
	withLanguageContext(HeroSection, ['selectedLang']),
	['globalData']
);
